import React, { useCallback, useEffect, useState } from "react";
import { Box, Chip, Collapse, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppButton } from "../../../components/form";
import ResponseRenderer from "./ResponseRenderer";
import { useChatHost } from "./chatHost";
import {
  isTerminalJobStatus,
  warehouseQueryJobService,
  type WarehouseQueryJobStatus,
  type WarehouseQueryJobView,
} from "../../../services/warehouseQueryJobService";

/** Poll as a safety net next to WebSocket pushes (a frame can land before the subscription connects). */
const POLL_MS = 10_000;

const STATUS_COLOR: Record<WarehouseQueryJobStatus, "default" | "info" | "success" | "error" | "warning"> = {
  QUEUED: "default",
  RUNNING: "info",
  SUCCEEDED: "success",
  FAILED: "error",
  CANCELLED: "warning",
};

export function formatBytes(bytes: number | undefined): string | undefined {
  if (bytes == null || !Number.isFinite(bytes)) return undefined;
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`;
}

export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, "0")}s` : `${s}s`;
}

interface Props {
  correlationId?: string;
  jobId: string;
  adaptorName?: string;
  sql?: string;
}

/**
 * Chat query card for a warehouse (Trino / ClickHouse) query that outlived the live window: status, engine progress,
 * elapsed time, cancel, and the result table once the background job succeeds.
 */
export default function WarehouseJobCard({ correlationId, jobId, adaptorName, sql }: Props) {
  const { t } = useTranslation();
  const [job, setJob] = useState<WarehouseQueryJobView | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();
  const [cancelling, setCancelling] = useState(false);
  const [showSql, setShowSql] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [exporting, setExporting] = useState(false);
  const { features } = useChatHost();

  const status = job?.status;
  const terminal = isTerminalJobStatus(status);

  const refresh = useCallback(async () => {
    try {
      setJob(await warehouseQueryJobService.get(jobId));
      setLoadError(undefined);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, [jobId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (terminal || !correlationId) return undefined;
    let unsubscribe: (() => void) | undefined;
    let disposed = false;
    void warehouseQueryJobService
      .subscribe(correlationId, jobId, (next) => setJob(next))
      .then((stop) => {
        if (disposed) stop();
        else unsubscribe = stop;
      });
    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, [correlationId, jobId, terminal]);

  useEffect(() => {
    if (terminal) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(() => void refresh(), POLL_MS);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [terminal, refresh]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      setJob(await warehouseQueryJobService.cancel(jobId));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setCancelling(false);
    }
  };

  const exportRunning = job?.exportStatus === "RUNNING";
  useEffect(() => {
    if (!exportRunning) return undefined;
    const poll = setInterval(() => void refresh(), 3_000);
    return () => clearInterval(poll);
  }, [exportRunning, refresh]);

  const handleExport = async () => {
    setExporting(true);
    try {
      setJob(await warehouseQueryJobService.startExport(jobId));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  };

  const handleDownload = async () => {
    try {
      await warehouseQueryJobService.downloadExport(jobId);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  };

  const startedMs = job?.startedAt ? Date.parse(job.startedAt) : job?.queuedAt ? Date.parse(job.queuedAt) : undefined;
  const elapsed =
    job?.durationMs != null ? job.durationMs : startedMs != null && !Number.isNaN(startedMs) ? now - startedMs : undefined;
  const percent = job?.progressPercent;
  const stats = [
    elapsed != null ? t("chat.warehouseJob.elapsed", { value: formatElapsed(elapsed) }) : undefined,
    job?.processedRows != null ? t("chat.warehouseJob.rows", { value: job.processedRows.toLocaleString() }) : undefined,
    formatBytes(job?.processedBytes) ? t("chat.warehouseJob.scanned", { value: formatBytes(job?.processedBytes) }) : undefined,
    !job?.processedBytes && formatBytes(job?.estimatedBytes)
      ? t("chat.warehouseJob.estimated", { value: formatBytes(job?.estimatedBytes) })
      : undefined,
  ].filter(Boolean);
  const querySql = job?.sql || sql;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5 }} data-testid="warehouse-job-card">
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
          {t("chat.warehouseJob.title", { name: job?.adaptorName || adaptorName || job?.engine || "" })}
        </Typography>
        <Chip
          size="small"
          color={status ? STATUS_COLOR[status] : "default"}
          label={t(`chat.warehouseJob.status.${status || "QUEUED"}`)}
        />
        {status === "QUEUED" && job?.queuePosition != null && (
          <Chip size="small" variant="outlined" label={t("chat.warehouseJob.queuePosition", { value: job.queuePosition })} />
        )}
        {job?.cachedAt && (
          <Chip size="small" variant="outlined" color="success" label={t("chat.warehouseJob.cached")} />
        )}
        {!terminal && (
          <AppButton
            size="small"
            color="error"
            variant="outlined"
            disabled={cancelling || !job || job.cancelRequested}
            onClick={() => void handleCancel()}
          >
            {job?.cancelRequested ? t("chat.warehouseJob.cancelling") : t("chat.warehouseJob.cancel")}
          </AppButton>
        )}
      </Stack>

      {!terminal && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress
            variant={percent != null ? "determinate" : "indeterminate"}
            value={percent != null ? Math.min(100, Math.max(0, percent)) : undefined}
          />
        </Box>
      )}

      {stats.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
          {percent != null && !terminal ? `${Math.round(percent)}% · ` : ""}
          {stats.join(" · ")}
        </Typography>
      )}

      {status === "FAILED" && job?.error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {job.error}
        </Typography>
      )}
      {status === "CANCELLED" && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("chat.warehouseJob.cancelled")}
        </Typography>
      )}
      {loadError && !job && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {loadError}
        </Typography>
      )}

      {querySql && (
        <Box sx={{ mt: 1 }}>
          <AppButton size="small" variant="text" onClick={() => setShowSql((v) => !v)}>
            {showSql ? t("chat.warehouseJob.hideSql") : t("chat.warehouseJob.showSql")}
          </AppButton>
          <Collapse in={showSql}>
            <Box
              component="pre"
              sx={{ m: 0, mt: 0.5, p: 1, fontSize: 12, overflowX: "auto", bgcolor: "action.hover", borderRadius: 1 }}
            >
              {querySql}
            </Box>
          </Collapse>
        </Box>
      )}

      {status === "SUCCEEDED" && job && (
        <Box sx={{ mt: 1 }}>
          {job.truncated && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              {t("chat.warehouseJob.truncated", { count: job.rowCount ?? 0 })}
            </Typography>
          )}
          {features.export && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
            {job.exportStatus === "SUCCEEDED" ? (
              <AppButton size="small" variant="outlined" onClick={() => void handleDownload()}>
                {t("chat.warehouseJob.download", { count: job.exportRowCount ?? 0 })}
              </AppButton>
            ) : (
              <AppButton
                size="small"
                variant="outlined"
                disabled={exporting || exportRunning}
                onClick={() => void handleExport()}
              >
                {exportRunning ? t("chat.warehouseJob.exporting") : t("chat.warehouseJob.export")}
              </AppButton>
            )}
            {job.exportStatus === "FAILED" && job.exportError && (
              <Typography variant="caption" color="error">
                {job.exportError}
              </Typography>
            )}
          </Stack>
          )}
          <ResponseRenderer
            payload={{
              correlationId: correlationId || "",
              type: "table",
              result: { columns: job.columns ?? [], rows: job.rows ?? [] },
              timestamp: job.finishedAt || new Date().toISOString(),
            }}
          />
        </Box>
      )}
    </Paper>
  );
}
